<template>
  <div
    v-if="isLoading && !isCreatingPaymentPlan"
    class="set-up-payment-plan pa-4"
  >
    <SoSkeleton height="2rem" />
    <SoSkeleton width="10rem" class="mt-8" />
    <div class="set-up-payment-plan__options mt-6">
      <SoSkeleton height="130px" />
      <SoSkeleton height="130px" />
      <SoSkeleton height="130px" />
    </div>
    <SoSkeleton width="16rem" class="mt-12" />
    <SoSkeleton height="3rem" width="200px" class="my-5" />
    <SoSkeleton width="18rem" class="my-16" />
    <SoSkeleton v-for="i in 6" :key="i" class="mt-3" />
  </div>
  <SoWidget v-else-if="planOptions" class="set-up-payment-plan">
    <h3 class="text-h3 mb-6">Set up Payment Plan</h3>

    <SoForm @submit="createPaymentPlan">
      <h5 class="text-h5 mb-6">Choose plan length</h5>

      <SoRadioCards v-model="planLengthOption" class="mb-6">
        <div class="set-up-payment-plan__options">
          <SoRadioCard
            v-for="(value, index) in planOptions.recommendedPlanLengthsInMonths"
            :key="index"
            :value="value"
          >
            <template v-if="index === 0" #badge>
              <SoTooltip
                text="
                  Start by offering this payment plan to the customer.
                  If they can't afford it, explore the other options
                  to give the customer more time to pay.
                "
              >
                <div class="d-flex align-center">
                  <p>Recommended</p>
                  <SoIcon
                    icon="info"
                    class="ml-1 d-inline-flex"
                    size="x-small"
                  />
                </div>
              </SoTooltip>
            </template>
            <template #title>{{ value }} Months Plan</template>

            <div class="d-inline-block w-50">
              <p class="text-h4">
                {{ getDebtMonthlyPaymentFormatted(value) }}
              </p>
              <p>Monthly</p>
            </div>

            <div class="d-inline-block w-50">
              <p class="text-h4">
                {{ getDebtYearlyPaymentFormatted(value) }}
              </p>
              <p>Yearly</p>
            </div>
          </SoRadioCard>

          <SoRadioCard :value="CustomPlanLength">
            <template #title>Custom Plan</template>

            <SoSelect
              v-model="customPlanLength"
              :items="customPlanLengthOptions"
              :rules="planLengthOption === CustomPlanLength ? [required] : []"
              placeholder="Choose plan length"
              class="mb-5"
              data-testid="custom-plan-length-selector"
              @update:model-value="planLengthOption = CustomPlanLength"
            />

            <template v-if="customPlanLength">
              <div class="d-inline-block w-50">
                <p class="text-h4">
                  {{ getDebtMonthlyPaymentFormatted(Number(customPlanLength)) }}
                </p>
                <p>Monthly</p>
              </div>

              <div class="d-inline-block w-50">
                <p class="text-h4">
                  {{ getDebtYearlyPaymentFormatted(Number(customPlanLength)) }}
                </p>
                <p>Yearly</p>
              </div>
            </template>
          </SoRadioCard>
        </div>
      </SoRadioCards>

      <h5
        v-if="!isDebtRecovery"
        class="text-h5 mb-4"
        data-testid="debt-payment-schedule-title"
      >
        Schedule Debt Payment Plan
      </h5>
      <div
        v-if="!isDebtRecovery"
        data-testid="debt-payment-select-payment-day"
        class="d-flex"
      >
        <SoSelect
          v-model="paymentDay"
          :items="allowedDays"
          :rules="[required]"
          label="Payment Day"
          placeholder="Choose day"
          class="d-inline-block"
          type="simple"
        />
      </div>

      <p
        v-if="!isDebtRecovery && fuelMonthlyPaymentDay"
        data-testid="debt-payment-select-payment-day"
        class="text-h6 mb-6"
      >
        <i>
          The monthly fuel payment is taken on the
          <strong>{{ formatDay(fuelMonthlyPaymentDay) }}</strong>
          of every month.
        </i>
      </p>

      <SoAlert
        v-if="requiresApproval"
        class="my-6"
        data-testid="requires-approval-alert"
      >
        You can approve a payment plan length of up to 24 months. If the
        customer
        <strong>really</strong> needs a longer time to pay

        <ul class="bulletpoint-list">
          <li>
            They will need to discuss a longer plan with our collections
            specialists – inform the customer of this and that the Collections
            Team will be able to help them to find a solution for them
          </li>
          <li>
            Warm transfer the customer to the Collections Team – please allow at
            least one minute for an agent to become available
          </li>
          <li>
            If no agent is available, raise a Task to the Collections Team and
            they will get back in touch with the customer as soon as possible to
            talk through their plan
          </li>
        </ul>
      </SoAlert>

      <SoSummaryTable
        :items="tableItems"
        :summary="tableSummary"
        title="Payment Plan Calculation Summary"
        class="mt-12"
      />

      <div class="d-flex gap-3 mt-12 mb-6">
        <SoButton
          :to="{ name: RouteNames.CustomerFinancials }"
          type="secondary"
          title="Cancel"
          large
        />
        <SoButton
          :loading="isCreatingPaymentPlan"
          :disabled="requiresApproval || isCreatingPaymentPlan"
          :title="
            requiresApproval ? 'Requires Approval' : 'Proceed with Amount'
          "
          type="primary"
          submit
          large
          data-testid="create-payment-plan-submit-button"
        />
      </div>

      <SoAlert
        v-if="errorCode === Errors.NO_PAYMENT_PLAN_CREATED_ON_JUNIFER"
        type="error"
        data-testid="debt-payment-plan-failure-error"
      >
        Payment schedule changed for fuel payment but the debt payment plan
        could not be created. Please refresh the page, try again or investigate
        the payment plan creation.
      </SoAlert>

      <SoAlert
        v-else-if="errorCode === Errors.UNKNOWN_ERROR"
        type="error"
        data-testid="debt-payment-plan-general-error"
      >
        {{
          error ??
          "Something went wrong. Please refresh the page and try again."
        }}
      </SoAlert>

      <SoDialog
        v-else
        v-model="showPSPFailureWarning"
        card-title="Failed to update fuel payment schedule"
        @on-close="
          (refetchData(), $router.push({ name: RouteNames.CustomerFinancials }))
        "
      >
        <p data-testid="debt-payment-plan-psp-failure-error">
          Debt Payment Plan created but the Fuel Payment could not be set.
          Please check the customers payment schedule and investigate if it can
          be adjusted to the fuel payment.
        </p>

        <div class="d-flex justify-end mt-8 gap-2">
          <SoButton
            :to="{ name: RouteNames.CustomerFinancials }"
            title="Close"
            large
            @click="refetchData()"
          />
          <SoButton
            :to="{ name: RouteNames.CustomerReviewPaymentAmount }"
            title="Review Fuel Payment Schedule"
            icon="arrow-up-right"
            type="primary"
            large
            @click="refetchData()"
          />
        </div>
      </SoDialog>
    </SoForm>
  </SoWidget>
</template>

<script lang="ts">
  import SoAlert from "@/components/SoAlert.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import SoRadioCard from "@/components/SoRadioCard.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoRadioCards from "@/components/SoRadioCards.vue"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import SoSummaryTable from "@/components/SoSummaryTable.vue"
  import SoTooltip from "@/components/SoTooltip.vue"
  import SoWidget from "@/components/SoWidget.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoForm from "@/components/formControls/SoForm.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import { formatDay } from "@/helpers/dateHelper"
  import { formatMoney } from "@/helpers/moneyHelpers"
  import { firstPaymentDate } from "@/helpers/paymentHelper"
  import { DebtPaymentPlanMutator } from "@/mutators/debtPaymentPlan/DebtPaymentPlanMutator"
  import { errorReporter } from "@/services/ErrorService"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { SoSummaryTableItem } from "@/types/components/SoSummaryTable"
  import { RouteNames } from "@/types/router"
  import required from "@/validators/required"
  import { defineComponent } from "vue"
  import { BackofficeErrors as Errors } from "@/constants/errors"
  import { DEBT_RECOVERY } from "@/constants/paymentPlan"
  import { isBackofficeApiError } from "@/api/backofficeRest/backofficeApiError"

  const MinDaysInAdvance = 8
  const CustomPlanLength = "custom" as const
  const allowedDays = Array.from({ length: 28 }, (_, i) => (i + 1).toString()) // 1 - 28

  export default defineComponent({
    components: {
      SoWidget,
      SoDialog,
      SoTooltip,
      SoForm,
      SoRadioCards,
      SoRadioCard,
      SoAlert,
      SoSelect,
      SoButton,
      SoSummaryTable,
      SoIcon,
      SoSkeleton,
    },

    data() {
      return {
        Errors,
        current: useCurrentAccountStore(),
        formatMoney,
        formatDay,
        required,
        RouteNames,
        CustomPlanLength,
        allowedDays,
        isCreatingPaymentPlan: false,
        planLengthOption: undefined as
          number | typeof CustomPlanLength | undefined,
        customPlanLength: undefined as string | undefined,
        paymentDay: undefined as string | undefined,
        error: null as string | null,
        errorCode: null as string | null,
        showPSPFailureWarning: false,
      }
    },

    computed: {
      isLoading() {
        return (
          this.current.nextPaymentSchedule.isFetching ||
          this.current.debtPaymentPlanEligibility.isFetching ||
          this.current.debtPaymentPlanOptions.isFetching ||
          this.current.coreId.isFetching
        )
      },
      customPlanLengthOptions() {
        if (!this.planOptions) return []
        const min = this.planOptions.minPlanLengthInMonths
        const max = this.planOptions.maxPlanLengthWithApprovalInMonths
        return Array.from({ length: max - min + 1 }, (_, i) => {
          const val = (i + min).toString()
          return {
            title: `${val} months`,
            value: val,
          }
        })
      },
      planOptions() {
        return this.current.debtPaymentPlanOptions.data
      },
      planLength() {
        return this.planLengthOption === CustomPlanLength
          ? Number(this.customPlanLength)
          : this.planLengthOption
      },
      recommendedPlanLength() {
        return this.planOptions?.recommendedPlanLengthsInMonths[0]
      },
      debtMonthlyPayment() {
        if (!this.planLength) return undefined
        return this.getDebtMonthlyPayment(this.planLength)
      },
      requiresApproval() {
        if (!this.planOptions) return false
        const max = this.planOptions.maxPlanLengthInMonths
        return !!this.planLength && this.planLength > max
      },
      firstPaymentDate() {
        if (!this.paymentDay) return
        return firstPaymentDate(Number(this.paymentDay), MinDaysInAdvance)
      },
      fuelMonthlyPaymentDay() {
        return this.current.nextPaymentSchedule.data?.paymentDay
      },
      totalMonthlyPayment() {
        if (!this.debtMonthlyPayment) return undefined
        const fuel = this.planOptions?.fuelMonthlyPayment ?? 0
        return this.debtMonthlyPayment + fuel
      },
      tableItems(): SoSummaryTableItem[] {
        return [
          {
            title: "Plan length",
            value: this.planLength,
            append: " months",
          },
          {
            type: "DATE",
            title: "First payment date",
            value: this.firstPaymentDate,
          },
          {
            title: "Payment day",
            value: this.paymentDay
              ? formatDay(Number(this.paymentDay))
              : undefined,
            append: " of the month",
          },
          {
            type: "MONEY_COLORED",
            title: "Total debt",
            value: this.planOptions?.totalDebt,
          },
          {
            type: "MONEY",
            title: "Debt monthly payment",
            value: this.debtMonthlyPayment,
          },
          {
            type: "MONEY",
            title: "Fuel monthly payment",
            value: this.planOptions?.fuelMonthlyPayment || undefined,
          },
        ]
      },
      tableSummary(): SoSummaryTableItem {
        return {
          type: "MONEY",
          title: "Total monthly payment",
          value: this.totalMonthlyPayment,
          append: "/month",
        }
      },
      isDebtRecovery() {
        return (
          this.current.debtPaymentPlanOptions.data?.planType === DEBT_RECOVERY
        )
      },
    },
    watch: {
      fuelMonthlyPaymentDay: {
        immediate: true,
        handler(newVal: number) {
          if (newVal || newVal === 0) {
            this.paymentDay = newVal.toString()
          }
        },
      },
      recommendedPlanLength: {
        immediate: true,
        handler(newVal: typeof this.recommendedPlanLength) {
          if (newVal && !this.planLengthOption) {
            this.planLengthOption = newVal
          }
        },
      },
    },
    methods: {
      getDebtMonthlyPayment(planLengthInMonths: number) {
        if (!this.planLength || !this.planOptions?.totalDebt) return 0
        // Matches calculation done in the backend.
        const debtInPennies = this.planOptions.totalDebt * 100
        return Math.ceil(debtInPennies / planLengthInMonths) / 100
      },
      getDebtMonthlyPaymentFormatted(planLengthInMonths: number) {
        return formatMoney({
          amount: this.getDebtMonthlyPayment(planLengthInMonths),
        })
      },
      getDebtYearlyPayment(planLengthInMonths: number) {
        return this.getDebtMonthlyPayment(planLengthInMonths) * 12
      },
      getDebtYearlyPaymentFormatted(planLengthInMonths: number) {
        return formatMoney({
          amount: this.getDebtYearlyPayment(planLengthInMonths),
        })
      },
      async createPaymentPlan() {
        this.error = null
        this.errorCode = null
        this.isCreatingPaymentPlan = true
        const juniferAccountId = this.current.account.data.juniferId
        const billingAccountId = this.current.coreId.data

        try {
          if (!this.planLength || !billingAccountId || !this.firstPaymentDate) {
            throw new Error("Missing required data")
          }
          await DebtPaymentPlanMutator.createForAccount(
            juniferAccountId,
          ).mutateAsync({
            billingAccountId,
            debtPlanLengthInMonths: this.planLength,
            ...(!this.isDebtRecovery && {
              debtPlanFirstPaymentDate: this.firstPaymentDate,
            }),
          })
          await this.refetchData()
          this.$router.push({ name: RouteNames.CustomerFinancials })
        } catch (error) {
          this.onError(error)
          errorReporter.report(error)
          console.error(error)
        }
        this.isCreatingPaymentPlan = false
      },
      onError(error) {
        this.errorCode = isBackofficeApiError(error)
          ? (error.errorCode ?? Errors.UNKNOWN_ERROR)
          : Errors.UNKNOWN_ERROR

        if (this.errorCode == Errors.NO_PAYMENT_SCHEDULE_CREATED_ON_JUNIFER) {
          this.showPSPFailureWarning = true
        }
      },
      refetchData() {
        return Promise.all([
          this.current.paymentSchedules.refetch(),
          this.current.debtPaymentPlans.refetch(),
        ])
      },
    },
  })
</script>
<style scoped>
  .set-up-payment-plan {
    max-width: 700px;
  }

  .set-up-payment-plan__options {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 1rem;
  }
</style>
